'use client'

import { useState } from 'react'
import { getSession, signIn } from 'next-auth/react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'

interface PdfDownloadCardProps {
  filename: string
  downloadUrl: string
  title?: string
}

export function PdfDownloadCard({ filename, downloadUrl, title }: PdfDownloadCardProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDownload = async () => {
    setError(null)
    setIsDownloading(true)

    try {
      const session = await getSession()

      if (session?.error === 'RefreshAccessTokenError') {
        await signIn('keycloak')
        throw new Error('Session expired. Please sign in again.')
      }

      const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
      const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''
      const fullUrl = downloadUrl.startsWith('http')
        ? downloadUrl
        : `${API_URL}${BASE_PATH}${downloadUrl}`

      const headers: Record<string, string> = {}
      if (session?.accessToken) {
        headers['Authorization'] = `Bearer ${session.accessToken}`
      }

      const response = await fetch(fullUrl, { method: 'GET', headers })

      if (response.status === 401) {
        await signIn('keycloak')
        throw new Error('Your session has expired. Please sign in again.')
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Failed to download the document.',
        )
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename
      anchor.rel = 'noopener noreferrer'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div
      className={cn(
        'mt-3 flex items-center gap-3 rounded-xl p-3',
        'border border-brand-cornflower/20',
        'bg-gradient-to-r from-brand-cornflower/5 to-brand-purple/5',
      )}
      role="region"
      aria-label="Generated PDF document"
    >
      <div
        className={cn(
          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg',
          'bg-gradient-to-br from-brand-navy to-brand-purple',
          'shadow-sm',
        )}
        aria-hidden="true"
      >
        <Icons.fileText className="h-5 w-5 text-white" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-brand-navy truncate">
          {title || filename}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {error ? error : 'Generated successfully'}
        </p>
      </div>

      <Button
        size="sm"
        onClick={handleDownload}
        disabled={isDownloading}
        className={cn(
          'flex-shrink-0 h-8 gap-1.5 rounded-full text-xs',
          'bg-gradient-to-r from-brand-navy to-brand-purple text-white',
          'hover:opacity-90 disabled:opacity-60',
        )}
        aria-label={`Download ${filename}`}
      >
        {isDownloading ? (
          <span
            className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin"
            aria-hidden="true"
          />
        ) : (
          <Icons.download className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
        )}
        {isDownloading ? 'Downloading…' : 'Download'}
      </Button>
    </div>
  )
}
